import type { OnboardingRequest } from '@console/types'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError } from '../api.js'
import {
  useAcceptNamedOnboardingRequest,
  useConfirmMergeOtp,
  useOnboardingRequests,
  useRejectOnboardingRequest,
  useRequestMergeOtp,
} from '../onboarding-requests/queries.js'
import { useTenants } from '../tenants/queries.js'
import { Button, HairlineRule, Input, Select, Skeleton } from '../ui/index.js'
import styles from './OnboardingQueuePage.module.css'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * `LGH-034`, `SPEC-ONB-001`, `DR-030`. The onboarding request queue — the
 * screen where a stranger becomes a tenant, or does not. Staff-only
 * (`RequireAuth`, the same guard `/rfq-source-invites` and `/runs` use), the
 * first tenant-less surface in this app.
 *
 * ── WHY MERGE IS ITS OWN SUB-FLOW, NOT A THIRD BUTTON ──────────────────────
 * `§2`: merge is the one outcome that's a privilege escalation — it grants a
 * stranger's phone standing access to a real tenant's data. Rendering the
 * three outcomes as equal buttons would get merge clicked at the same rate
 * as the other two. So *Accept and name* and *Reject* sit as simple, same-
 * tier controls; opening "Merge into an existing tenant" reveals a separate,
 * heavier flow — pick the tenant, see what's being authorised, send a code,
 * enter it — that a human has to deliberately walk into.
 * ═══════════════════════════════════════════════════════════════════════════
 */

const TAB_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'rejected', label: 'Rejected' },
] as const

type Tab = (typeof TAB_OPTIONS)[number]['value']

function MergeFlow({ request }: { request: OnboardingRequest }) {
  const tenants = useTenants()
  const [open, setOpen] = useState(false)
  const [tenantId, setTenantId] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [otp, setOtp] = useState('')
  const requestOtp = useRequestMergeOtp()
  const confirmOtp = useConfirmMergeOtp()

  const pickedTenant = tenants.data?.tenants.find((t) => t.id === tenantId)

  if (!open) {
    return (
      <button
        type="button"
        className={styles['mergeToggle']}
        onClick={() => {
          setOpen(true)
        }}
      >
        Merge into an existing tenant…
      </button>
    )
  }

  return (
    <div className={styles['mergeFlow']}>
      <p className={styles['mergeWarning']}>
        This grants <strong>{request.sender.value}</strong> standing access to whichever tenant you
        pick below. A code will be sent to that tenant&apos;s own admin — never to the sender.
      </p>

      <Select
        label="Merge into"
        options={[
          { value: '', label: 'Choose a tenant…' },
          ...(tenants.data?.tenants.map((t) => ({ value: t.id, label: t.displayName })) ?? []),
        ]}
        value={tenantId}
        onChange={(event) => {
          setTenantId(event.target.value)
          setOtpSent(false)
        }}
      />

      {pickedTenant !== undefined && !otpSent && (
        <Button
          variant="secondary"
          disabled={requestOtp.isPending}
          onClick={() => {
            requestOtp.mutate(
              { id: request.id, tenantId },
              {
                onSuccess: () => {
                  setOtpSent(true)
                },
              },
            )
          }}
        >
          {requestOtp.isPending
            ? 'Sending…'
            : `Send confirmation code to ${pickedTenant.displayName}'s admin`}
        </Button>
      )}

      {otpSent && (
        <div className={styles['otpRow']}>
          <Input
            label="Confirmation code"
            value={otp}
            onChange={(event) => {
              setOtp(event.target.value)
            }}
          />
          <Button
            disabled={otp.trim() === '' || confirmOtp.isPending}
            onClick={() => {
              confirmOtp.mutate({ id: request.id, tenantId, otp: otp.trim() })
            }}
          >
            {confirmOtp.isPending ? 'Confirming…' : 'Confirm merge'}
          </Button>
        </div>
      )}

      {confirmOtp.isError && (
        <p role="alert" className={styles['error']}>
          {confirmOtp.error instanceof ApiError
            ? confirmOtp.error.message
            : 'That code was not accepted.'}
        </p>
      )}
    </div>
  )
}

function RequestCard({ request }: { request: OnboardingRequest }) {
  const [tenantName, setTenantName] = useState('')
  const acceptNamed = useAcceptNamedOnboardingRequest()
  const reject = useRejectOnboardingRequest()
  const isPending = request.status === 'pending'

  return (
    <li className={styles['card']}>
      <p className={styles['sender']}>{request.sender.value}</p>
      <p className={styles['message']}>{request.messageText}</p>
      {request.attachment !== null && (
        <p className={styles['meta']}>Attachment: {request.attachment.sha256.slice(0, 12)}…</p>
      )}
      {request.qrSourceContext !== null && (
        <p className={styles['meta']}>Source: {request.qrSourceContext}</p>
      )}

      {isPending ? (
        <>
          <div className={styles['peerActions']}>
            <Input
              label="Name this tenant"
              value={tenantName}
              onChange={(event) => {
                setTenantName(event.target.value)
              }}
              placeholder="Acme Trading"
            />
            <Button
              disabled={tenantName.trim() === '' || acceptNamed.isPending}
              onClick={() => {
                acceptNamed.mutate({ id: request.id, tenantName: tenantName.trim() })
              }}
            >
              {acceptNamed.isPending ? 'Accepting…' : 'Accept and name'}
            </Button>
            <Button
              variant="secondary"
              disabled={reject.isPending}
              onClick={() => {
                reject.mutate(request.id)
              }}
            >
              {reject.isPending ? 'Rejecting…' : 'Reject'}
            </Button>
          </div>

          <HairlineRule />
          <MergeFlow request={request} />
        </>
      ) : (
        <p className={styles['status']}>Rejected</p>
      )}
    </li>
  )
}

export function OnboardingQueuePage() {
  const [tab, setTab] = useState<Tab>('pending')
  const requests = useOnboardingRequests(tab)

  return (
    <main className={styles['page']}>
      <div className={styles['header']}>
        <h1>Onboarding requests</h1>
        {/*
          `LGH-046`. The other door: a customer who calls or emails instead
          of messaging WhatsApp first has no `OnboardingRequest` to appear
          here at all — this is where an operator creates the tenant
          directly instead.
        */}
        <Link to="/tenants/new" className={styles['createLink']}>
          Create a tenant directly →
        </Link>
      </div>

      <div className={styles['tabs']} role="tablist">
        {TAB_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={tab === option.value}
            className={styles['tab']}
            data-active={tab === option.value}
            onClick={() => {
              setTab(option.value)
            }}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/*
        Operator-console UX audit finding. This was `<p>Loading…</p>` — the
        only bare-text loading state left anywhere in the operator console;
        every sibling screen (Home, Health, Runs, Ask) already uses
        `Skeleton`. The error line below was likewise the only uncoloured
        `role="alert"` on this page — `--stop` added to match every other
        screen's own convention.
      */}
      {requests.isPending && (
        <div className={styles['list']}>
          <Skeleton height="4rem" />
          <Skeleton height="4rem" />
          <Skeleton height="4rem" />
        </div>
      )}
      {requests.isError && (
        <p role="alert" style={{ color: 'var(--stop)' }}>
          The queue could not be loaded.
        </p>
      )}
      {requests.data !== undefined && (
        <ul className={styles['list']}>
          {requests.data.length === 0 && <p className={styles['empty']}>Nothing here.</p>}
          {requests.data.map((request) => (
            <RequestCard key={request.id} request={request} />
          ))}
        </ul>
      )}
    </main>
  )
}
